// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the menu under a globe's time chip (docs/AYNI_XR_MULTI_GLOBE_PLAN.md,
 * phase 1): how globe N's date follows globe 1. The short list first
 * (owner, 2026-10-10): the same date, 1, 10 and 30 years earlier, 3 and 6
 * months later; then another offset (a number and a unit) and a fixed
 * date. One menu element for the page, placed under the chip, closed by
 * Escape, a click outside or a choice. Styled as the dataset menu.
 */

import { plural, t } from '../i18n'
import { escapeAttr, escapeHtml } from './domUtils'
import {
  FOLLOW,
  OFFSET_PRESETS,
  isoDay,
  offsetOf,
  offsetWithinLimits,
  sameRule,
  type GlobeTimeRule,
  type TimeOffset,
} from '../services/globeSlots'

export interface GlobeTimeMenuOptions {
  /** The globe's number as people see it (2, 3, 4). */
  globe: number
  current: GlobeTimeRule
  onChoose(rule: GlobeTimeRule): void
}

const MENU_ID = 'globe-time-menu'

let openFor: { anchor: HTMLElement } | null = null

/** The chip's short text for a rule: "Same date", "−30 yr", "+3 mo", "Held on 2015-06-01". */
export function timeRuleLabel(rule: GlobeTimeRule): string {
  if (rule.kind === 'follow') return t('viewport.time.follow')
  if (rule.kind === 'fixed') return t('viewport.time.fixed', { date: isoDay(rule.ms) })
  return offsetLabel(rule.offset)
}

/** An offset in its coarsest whole unit, signed with a true minus: "−30 yr", "+3 mo", "+5 d". */
export function offsetLabel(o: TimeOffset): string {
  const sign = (n: number) => (n < 0 ? '−' : '+') + Math.abs(n)
  if (o.days !== 0 && o.months === 0 && o.years === 0) return t('viewport.time.days', { n: sign(o.days) })
  const months = o.years * 12 + o.months
  if (o.days === 0 && months % 12 === 0 && months !== 0) return t('viewport.time.years', { n: sign(months / 12) })
  if (o.days === 0) return t('viewport.time.months', { n: sign(months) })
  return t('viewport.time.days', { n: sign(o.years * 365 + o.months * 30 + o.days) })
}

/** A preset as a menu line: "30 years earlier", "1 year earlier", "3 months later". */
export function presetLabel(o: TimeOffset): string {
  const months = o.years * 12 + o.months
  const line = (n: number, back: 'viewport.time.preset.daysBack' | 'viewport.time.preset.monthsBack' | 'viewport.time.preset.yearsBack', ahead: 'viewport.time.preset.daysAhead' | 'viewport.time.preset.monthsAhead' | 'viewport.time.preset.yearsAhead') =>
    plural(Math.abs(n), { one: `${n < 0 ? back : ahead}.one`, other: `${n < 0 ? back : ahead}.other` }, { n: Math.abs(n) })
  if (o.days !== 0) return line(o.days, 'viewport.time.preset.daysBack', 'viewport.time.preset.daysAhead')
  if (months % 12 === 0) return line(months / 12, 'viewport.time.preset.yearsBack', 'viewport.time.preset.yearsAhead')
  return line(months, 'viewport.time.preset.monthsBack', 'viewport.time.preset.monthsAhead')
}

function ensureMenu(): HTMLElement {
  let el = document.getElementById(MENU_ID)
  if (!el) {
    el = document.createElement('div')
    el.id = MENU_ID
    el.className = 'dataset-menu globe-time-menu hidden'
    el.setAttribute('role', 'menu')
    document.body.appendChild(el)
    el.addEventListener('keydown', onKeydown)
    document.addEventListener('pointerdown', (e) => {
      if (openFor && !el!.contains(e.target as Node) && !openFor.anchor.contains(e.target as Node)) closeGlobeTimeMenu()
    }, true)
    window.addEventListener('resize', () => closeGlobeTimeMenu())
  }
  return el
}

function items(el: HTMLElement): HTMLElement[] {
  return [...el.querySelectorAll<HTMLElement>('[role="menuitemradio"], [role="menuitem"]')]
}

function render(opts: GlobeTimeMenuOptions): string {
  const line = (rule: GlobeTimeRule, label: string, value: string) => {
    const on = sameRule(rule, opts.current)
    return `<button type="button" role="menuitemradio" aria-checked="${on}" class="dataset-menu-item globe-time-item" data-rule="${escapeAttr(value)}">`
      + `<span class="dataset-menu-check" aria-hidden="true">${on ? '&#x2713;' : ''}</span><span>${escapeHtml(label)}</span></button>`
  }
  let html = `<div class="dataset-menu-title" aria-hidden="true">${escapeHtml(t('viewport.time.menu.title', { n: opts.globe }))}</div>`
  html += line(FOLLOW, t('viewport.time.follow'), 'follow')
  for (const o of OFFSET_PRESETS) html += line({ kind: 'offset', offset: o }, presetLabel(o), `offset:${o.years}:${o.months}:${o.days}`)
  html += `<div class="dataset-menu-sep" role="separator"></div>`
  const custom = opts.current.kind === 'offset' && !OFFSET_PRESETS.some((o) => sameRule({ kind: 'offset', offset: o }, opts.current))
  const cur = opts.current.kind === 'offset' ? opts.current.offset : offsetOf(-20)
  const unit = cur.days !== 0 ? 'd' : cur.months !== 0 ? 'm' : 'y'
  const n = cur.days !== 0 ? cur.days : cur.months !== 0 ? cur.years * 12 + cur.months : cur.years
  html += `<div class="globe-time-custom" role="group" aria-label="${escapeAttr(t('viewport.time.menu.custom'))}">`
    + `<span class="globe-time-custom-label${custom ? ' is-current' : ''}">${escapeHtml(t('viewport.time.menu.custom'))}</span>`
    + `<span class="globe-time-custom-row">`
    + `<input type="number" class="globe-time-n" id="globe-time-n" value="${n}" step="1" min="-6000" max="6000" aria-label="${escapeAttr(t('viewport.time.menu.offsetLabel'))}">`
    + `<select class="globe-time-unit" id="globe-time-unit" aria-label="${escapeAttr(t('viewport.time.menu.unitLabel'))}">`
    + `<option value="d"${unit === 'd' ? ' selected' : ''}>${escapeHtml(t('viewport.time.menu.unit.days'))}</option>`
    + `<option value="m"${unit === 'm' ? ' selected' : ''}>${escapeHtml(t('viewport.time.menu.unit.months'))}</option>`
    + `<option value="y"${unit === 'y' ? ' selected' : ''}>${escapeHtml(t('viewport.time.menu.unit.years'))}</option>`
    + `</select>`
    + `<button type="button" role="menuitem" class="globe-time-apply" data-apply="offset">${escapeHtml(t('viewport.time.menu.apply'))}</button>`
    + `</span></div>`
  const fixed = opts.current.kind === 'fixed' ? isoDay(opts.current.ms) : ''
  html += `<div class="globe-time-custom" role="group" aria-label="${escapeAttr(t('viewport.time.menu.fixed'))}">`
    + `<span class="globe-time-custom-label${fixed ? ' is-current' : ''}">${escapeHtml(t('viewport.time.menu.fixed'))}</span>`
    + `<span class="globe-time-custom-row">`
    + `<input type="date" class="globe-time-date" id="globe-time-date" value="${escapeAttr(fixed)}" aria-label="${escapeAttr(t('viewport.time.menu.fixed'))}">`
    + `<button type="button" role="menuitem" class="globe-time-apply" data-apply="fixed">${escapeHtml(t('viewport.time.menu.apply'))}</button>`
    + `</span></div>`
  return html
}

function place(el: HTMLElement, anchor: HTMLElement): void {
  const margin = 8
  const rect = el.getBoundingClientRect()
  const a = anchor.getBoundingClientRect()
  let top = a.bottom + 4
  if (top + rect.height > window.innerHeight - margin) top = Math.max(margin, a.top - 4 - rect.height)
  let left = document.documentElement.dir === 'rtl' ? a.right - rect.width : a.left
  left = Math.max(margin, Math.min(left, window.innerWidth - rect.width - margin))
  el.style.top = `${Math.round(top)}px`
  el.style.left = `${Math.round(left)}px`
}

/** Open the menu under a globe's time chip; the same chip again closes it. */
export function openGlobeTimeMenu(anchor: HTMLElement, opts: GlobeTimeMenuOptions): void {
  if (openFor?.anchor === anchor) {
    closeGlobeTimeMenu(true)
    return
  }
  const el = ensureMenu()
  el.innerHTML = render(opts)
  el.classList.remove('hidden')
  openFor = { anchor }
  anchor.setAttribute('aria-expanded', 'true')
  place(el, anchor)

  const choose = (rule: GlobeTimeRule) => {
    closeGlobeTimeMenu(true)
    opts.onChoose(rule)
  }
  el.onclick = (e) => {
    const target = e.target as HTMLElement
    const item = target.closest<HTMLElement>('[data-rule]')
    if (item) {
      const v = item.dataset.rule ?? 'follow'
      if (v === 'follow') return choose(FOLLOW)
      const [, y, m, d] = v.split(':').map(Number)
      return choose({ kind: 'offset', offset: offsetOf(y, m, d) })
    }
    const apply = target.closest<HTMLElement>('[data-apply]')
    if (!apply) return
    if (apply.dataset.apply === 'offset') {
      const n = Math.trunc(Number((el.querySelector('#globe-time-n') as HTMLInputElement | null)?.value ?? ''))
      const unit = (el.querySelector('#globe-time-unit') as HTMLSelectElement | null)?.value ?? 'y'
      if (!Number.isFinite(n)) return
      if (n === 0) return choose(FOLLOW)
      const offset = unit === 'y' ? offsetOf(n) : unit === 'm' ? offsetOf(0, n) : offsetOf(0, 0, n)
      if (!offsetWithinLimits(offset)) return
      return choose({ kind: 'offset', offset })
    }
    const date = (el.querySelector('#globe-time-date') as HTMLInputElement | null)?.value ?? ''
    const ms = Date.parse(`${date}T00:00:00Z`)
    if (!date || !Number.isFinite(ms)) return
    choose({ kind: 'fixed', ms })
  }
  // The Enter key in a field applies that field.
  el.onkeydown = (e) => {
    if (e.key !== 'Enter') return
    const field = e.target as HTMLElement
    const row = field.closest<HTMLElement>('.globe-time-custom-row')
    const apply = row?.querySelector<HTMLElement>('[data-apply]')
    if (apply) {
      e.preventDefault()
      apply.click()
    }
  }
  const checked = el.querySelector<HTMLElement>('[aria-checked="true"]') ?? items(el)[0]
  checked?.focus()
}

export function closeGlobeTimeMenu(restoreFocus = false): void {
  const el = document.getElementById(MENU_ID)
  if (!el || !openFor) return
  el.classList.add('hidden')
  el.onclick = null
  el.onkeydown = null
  const anchor = openFor.anchor
  openFor = null
  anchor.setAttribute('aria-expanded', 'false')
  if (restoreFocus) anchor.focus()
}

export function isGlobeTimeMenuOpen(): boolean {
  return openFor !== null
}

function onKeydown(e: KeyboardEvent): void {
  const el = document.getElementById(MENU_ID)
  if (!el) return
  if (e.key === 'Escape') {
    e.preventDefault()
    closeGlobeTimeMenu(true)
    return
  }
  const target = e.target as HTMLElement
  if (target instanceof HTMLInputElement || target instanceof HTMLSelectElement) return
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    e.preventDefault()
    const list = items(el)
    const at = list.indexOf(document.activeElement as HTMLElement)
    const next = list[(at + (e.key === 'ArrowDown' ? 1 : list.length - 1)) % list.length]
    next?.focus()
  }
}
